import type { SpanContext } from '@opentelemetry/api';
import { type LogAttributes, SeverityNumber } from '@opentelemetry/api-logs';
import { ExportResult, ExportResultCode, hrTimeToMilliseconds } from '@opentelemetry/core';
import type { LogRecordExporter, ReadableLogRecord } from '@opentelemetry/sdk-logs';
import { ATTR_EXCEPTION_STACKTRACE } from '@opentelemetry/semantic-conventions';

interface PrintableLogRecord {
    severityNumber?: SeverityNumber;
    message: string;
    stacktrace?: string;
    details: { attributes: LogAttributes; spanContext: SpanContext };
}

export class ConsoleLogRecordExporter implements LogRecordExporter {
    export(logs: ReadableLogRecord[], resultCallback: (result: ExportResult) => void): void {
        logs.map(log => this.formatLog(log)).forEach(this.print);
        resultCallback?.({ code: ExportResultCode.SUCCESS });
    }

    private formatLog(log: ReadableLogRecord): PrintableLogRecord {
        const timestamp = hrTimeToMilliseconds(log.hrTimeObserved);
        const time = new Date(timestamp).toLocaleTimeString();
        const message = `${time} ${log.severityText?.toUpperCase()} [${log.attributes['label']}] ${log.body}`;
        const attributes = log.attributes;
        attributes['time'] = new Date(timestamp).toISOString();
        const details = JSON.parse(
            JSON.stringify({
                attributes: attributes,
                spanContext: log.spanContext
            })
        );
        return {
            severityNumber: log.severityNumber,
            message,
            stacktrace: attributes[ATTR_EXCEPTION_STACKTRACE] as string | undefined,
            details
        };
    }

    private print(log: PrintableLogRecord): void {
        switch (log.severityNumber) {
            case SeverityNumber.TRACE:
                // console.trace is to outputs a stack trace to the console! => use debug instead
                // see https://developer.mozilla.org/en-US/docs/Web/API/console/trace_static
                console.debug(log.message);
                break;
            case SeverityNumber.DEBUG:
                console.debug(log.message);
                break;
            case SeverityNumber.INFO:
                console.info(log.message);
                break;
            case SeverityNumber.WARN:
                console.warn(log.message);
                break;
            case SeverityNumber.ERROR:
                console.error(log.message, log.stacktrace ? `\n${log.stacktrace}\n` : '\n', log.details);
                break;
            default:
                console.log(log.message);
        }
    }

    shutdown(): Promise<void> {
        return this.forceFlush();
    }

    forceFlush(): Promise<void> {
        return Promise.resolve();
    }
}
